import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo
} from 'react';
import PropTypes from 'prop-types';
import { Select, Input, Popover } from 'antd';
import { SearchOutlined, FilterOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import { SearchBarContainer, AdvancedFiltersButton } from './styled';

const { Option } = Select;

const DEFAULT_FIELD = { value: '', label: 'Search by' };

const SEARCH_FIELDS = [
  DEFAULT_FIELD,
  { value: 'transactionId', label: 'Transaction ID' },
  { value: 'counterpartyId', label: 'Counterparty ID' },
  { value: 'counterpartyName', label: 'Counterparty Name' }
];

const MIN_SEARCH_LENGTH = 3;
const DEBOUNCE_DELAY = 300;

const TransactionSearchBar = ({
  onSearch,
  advancedFiltersOpen,
  onToggleAdvancedFilters,
  advancedFiltersOverlay,
  isTMTransaction = true
}) => {
  const searchFields = useMemo(() => {
    if (isTMTransaction) return SEARCH_FIELDS;
    return SEARCH_FIELDS.filter(
      (f) => f.value !== 'counterpartyId' && f.value !== 'counterpartyName'
    );
  }, [isTMTransaction]);

  const [searchField, setSearchField] = useState(DEFAULT_FIELD.value);
  const [searchValue, setSearchValue] = useState('');

  const debouncedSearch = useRef(
    debounce((field, value) => {
      if (value.length >= MIN_SEARCH_LENGTH) {
        onSearch({ searchField: field, searchValue: value });
      } else if (value.length === 0) {
        onSearch({ searchField: field, searchValue: '' });
      }
    }, DEBOUNCE_DELAY)
  ).current;

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    if (
      !isTMTransaction &&
      (searchField === 'counterpartyId' || searchField === 'counterpartyName')
    ) {
      setSearchField(DEFAULT_FIELD.value);
      setSearchValue('');
      onSearch({ searchField: '', searchValue: '' });
    }
  }, [isTMTransaction]);

  const handleInputChange = useCallback(
    (e) => {
      const value = e.target.value;
      setSearchValue(value);
      debouncedSearch(searchField, value);
    },
    [searchField, debouncedSearch]
  );

  const handleFieldChange = useCallback(
    (value) => {
      setSearchField(value);
      debouncedSearch.cancel();

      if (!value) {
        setSearchValue('');
        onSearch({ searchField: '', searchValue: '' });
      } else if (searchValue !== '') {
        onSearch({ searchField: value, searchValue });
      }
    },
    [searchValue, onSearch, debouncedSearch]
  );

  const handleClear = useCallback(() => {
    setSearchValue('');
    debouncedSearch.cancel();
    onSearch({ searchField, searchValue: '' });
  }, [searchField, onSearch, debouncedSearch]);

  const isSearchDisabled = !searchField;

  const getPlaceholder = () => {
    if (isSearchDisabled) return 'Select a search field...';
    const field = searchFields.find((f) => f.value === searchField);
    return `Search by ${field?.label || 'Transaction ID'}...`;
  };

  return (
    <SearchBarContainer>
      <Select
        value={searchField}
        onChange={handleFieldChange}
        style={{ width: 170 }}>
        {searchFields.map((f) => (
          <Option key={f.value} value={f.value}>
            {f.label}
          </Option>
        ))}
      </Select>
      <Input
        prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
        placeholder={getPlaceholder()}
        value={searchValue}
        onChange={handleInputChange}
        allowClear
        onClear={handleClear}
        disabled={isSearchDisabled}
        maxLength={1000}
        style={{ flex: 1 }}
      />
      <Popover
        content={advancedFiltersOverlay}
        trigger="click"
        open={advancedFiltersOpen}
        onOpenChange={onToggleAdvancedFilters}
        placement="bottomRight"
        overlayInnerStyle={{ padding: 0 }}>
        <AdvancedFiltersButton
          $active={advancedFiltersOpen}
          icon={<FilterOutlined />}>
          Advanced Filters
        </AdvancedFiltersButton>
      </Popover>
    </SearchBarContainer>
  );
};

TransactionSearchBar.propTypes = {
  onSearch: PropTypes.func.isRequired,
  advancedFiltersOpen: PropTypes.bool,
  onToggleAdvancedFilters: PropTypes.func,
  advancedFiltersOverlay: PropTypes.node,
  isTMTransaction: PropTypes.bool
};

export default TransactionSearchBar;
